import { format } from "date-fns";
import * as React from "react";
import { useEffect, useState } from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { ThunkDispatch } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementsById } from "core-integration/src/store/elements/types";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getGroupedFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import {
  IFixtureStat,
  IFixtureStats,
} from "core-integration/src/store/fixtures/types";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { dateLocales } from "../i18n";
import Button from "./Button";
import Fixture from "./Fixture";
import { Pager, PagerButton, PagerButtonNext } from "./Pager";
import { ControlArrowLeft, ControlArrowRight } from "./icons/Arrows";

const BackgroundColorWrapper = styled.div`
  padding: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.primary};
`;

const Deadline = styled.h2`
  margin: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[3]};
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
  font-weight: 600;
`;

const Heading = styled.h3`
  margin: ${({ theme }) => theme.space[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
`;

const Note = styled.p`
  text-align: center;
`;

const FixtureDay = styled.h4`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  text-align: center;
`;

const FixtureTime = styled.time`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.white};
`;

const FixtureList = styled.ul`
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureItem = styled.li`
  margin-bottom: ${({ theme }) => theme.space[2]};
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureButton = styled.button<IStyledFixtureStatsProps>`
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  text-align: left;
  cursor: pointer;

  ${(props) =>
    props.isOpen &&
    css`
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    `}
`;

const FixtureStatsWrap = styled.div<IStyledFixtureStatsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  padding: ${({ theme }) => `${theme.space[2]} 5%`};
  border-bottom-left-radius: ${({ theme }) => theme.radii[2]};
  border-bottom-right-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.blueDarker3};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    padding-right: 15%;
    padding-left: 15%;
  }
`;

const FixtureStatsOuterList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FixtureStat = styled.li`
  margin-bottom: ${({ theme }) => theme.space[4]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: white;
`;

const FixtureStatHeading = styled.h5`
  margin: 0;
  padding: ${({ theme }) => theme.space[1]} ${({ theme }) => theme.space[2]};
  border-bottom: ${({ theme }) => theme.borders[0]};
  border-top-left-radius: ${({ theme }) => theme.radii[2]};
  border-top-right-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  text-align: center;
`;

const FixtureStatBody = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.space[2]};
  padding-bottom: ${({ theme }) => theme.space[2]};
`;

const FixtureStatList = styled.ul`
  flex: 1;
  padding-right: ${({ theme }) => theme.space[2]};
  padding-left: ${({ theme }) => theme.space[2]};

  &:first-child {
    border-right: 1px dotted ${({ theme }) => theme.colors.black};
    text-align: right;
  }
`;

const FixtureStatItem = styled.li`
  padding-top: ${({ theme }) => theme.space[1]};
  padding-bottom: ${({ theme }) => theme.space[1]};
  border-bottom: 1px solid #e2e2e2;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  list-style: none;

  :last-child {
    border-bottom: 0;
  }
`;

const ElementButton = styled.button`
  background-color: transparent;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[2]};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

interface IFixtureStatsForTeamProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStat[];
}

const FixtureStatsForTeam: React.FC<IFixtureStatsForTeamProps> = ({
  elementsById,
  showElementDialog,
  stats,
}) => (
  <FixtureStatList>
    {stats
      .filter((fs) => elementsById[fs.element])
      .map((fs) => (
        <FixtureStatItem key={fs.element}>
          <ElementButton onClick={(_: any) => showElementDialog(fs.element)}>
            {elementsById[fs.element].web_name}
          </ElementButton>{" "}
          ({fs.value})
        </FixtureStatItem>
      ))}
  </FixtureStatList>
);

interface IFixtureStatsProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStats[];
  statsByIdentifier: Record<string, IElementStat>;
}

interface IStyledFixtureStatsProps {
  isOpen?: boolean;
}

type FixtureStatProps = IFixtureStatsProps & IStyledFixtureStatsProps;

const FixtureStats: React.FC<FixtureStatProps> = ({
  elementsById,
  isOpen = false,
  showElementDialog,
  stats,
  statsByIdentifier,
}) => {
  // Our render stats should contain only stats with data and a maximum
  // of 5 per team of BPS
  const renderStats = stats
    .filter((stat) => stat.h.length || stat.a.length)
    .map((stat) =>
      stat.identifier === "bps"
        ? {
            identifier: stat.identifier,
            h: stat.h.slice(0, 5),
            a: stat.a.slice(0, 5),
          }
        : stat,
    );
  return (
    <FixtureStatsWrap isOpen={isOpen}>
      <FixtureStatsOuterList>
        {renderStats.map((stat) => (
          <FixtureStat key={stat.identifier}>
            <FixtureStatHeading>
              {statsByIdentifier[stat.identifier].label}
            </FixtureStatHeading>
            <FixtureStatBody>
              <FixtureStatsForTeam
                stats={stat.h}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
              <FixtureStatsForTeam
                stats={stat.a}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
            </FixtureStatBody>
          </FixtureStat>
        ))}
      </FixtureStatsOuterList>
    </FixtureStatsWrap>
  );
};

interface IOwnProps {
  eventId?: number;
  useLinks?: boolean;
}

type Props = IOwnProps & WithTranslation;

declare global {
  interface Window {
    postscribe: any;
  }
}

const Fixtures = ({
  i18n,

  t,
  eventId: initialEventId,
  useLinks = false,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const currentEvent = useSelector(getCurrentEvent);
  const elementsById = useSelector(getElementsById);
  const eventsById = useSelector(getEventsById);
  const groupedFixturesByEvent = useSelector(getGroupedFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);
  const statsByIdentifier = useSelector(getElementStatsByIdentifier);

  const [eventId, setEventId] = useState(() => {
    if (initialEventId) {
      return initialEventId;
    } else if (nextEvent) {
      return nextEvent.id;
    } else if (currentEvent) {
      return currentEvent.id;
    }
    return 0;
  });

  useEffect(() => {
    dispatch(fetchFixtures(eventId));
  }, [dispatch, eventId]);

  const changeEvent = (newEventId: number) => {
    setEventId(newEventId);
    dispatch(fetchFixtures(newEventId));
  };

  const toggle = (fixtureId: number) => {
    setIsOpen((prevState) => ({
      ...prevState,
      [fixtureId]: !prevState[fixtureId],
    }));
  };

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const event = eventsById[eventId];
  if (!event) {
    return null;
  }

  const eventFixtureGroups = groupedFixturesByEvent[eventId] || [];
  return (
    <section>
      <BackgroundColorWrapper>
        <header>
          <Deadline>
            {event.name} -{" "}
            <time dateTime={formatRawAsISO(event.deadline_time)}>
              {formatRawAsLocalI18n(
                event.deadline_time,
                dateLocales[i18n.language],
              )}
            </time>
          </Deadline>
          <Heading>
            <span>{t("fixtures.title", "Fixtures")}</span>
          </Heading>
          <div>
            <Pager>
              {eventsById[eventId - 1] && (
                <PagerButton>
                  {useLinks ? (
                    <Button
                      width={1}
                      to={`/fixtures/${eventId - 1}`}
                      onClick={() => changeEvent(eventId - 1)}
                      startIcon={<ControlArrowLeft />}
                    >
                      {t("fixtures.previous", "Previous")}
                    </Button>
                  ) : (
                    <Button
                      width={1}
                      onClick={() => changeEvent(eventId - 1)}
                      startIcon={<ControlArrowLeft />}
                    >
                      {t("fixtures.previous", "Previous")}
                    </Button>
                  )}
                </PagerButton>
              )}
              {eventsById[eventId + 1] && (
                <PagerButtonNext>
                  {useLinks ? (
                    <Button
                      width={1}
                      to={`/fixtures/${eventId + 1}`}
                      onClick={() => changeEvent(eventId + 1)}
                      variant="primary"
                      endIcon={<ControlArrowRight />}
                    >
                      {t("fixtures.next", "Next")}
                    </Button>
                  ) : (
                    <Button
                      width={1}
                      onClick={() => changeEvent(eventId + 1)}
                      disabled={!eventsById[eventId + 1]}
                      variant="primary"
                      endIcon={<ControlArrowRight />}
                    >
                      {t("fixtures.next", "Next")}
                    </Button>
                  )}
                </PagerButtonNext>
              )}
            </Pager>
          </div>
        </header>
        <Box p={2}>
          <Note>
            <Trans i18nKey="fixtures.localTimes">
              All times are shown in your <strong>local time</strong>
            </Trans>
          </Note>
        </Box>
        {eventFixtureGroups.map((group) => (
          <Box mx="0.4rem" mb={4} key={group.date.toISOString()}>
            <FixtureDay>
              <FixtureTime dateTime={group.date.toISOString()}>
                {format(group.date, "EEEE d MMMM yyyy")}
              </FixtureTime>
            </FixtureDay>
            <FixtureList>
              {group.fixtures.map((f) => (
                <React.Fragment key={f.id}>
                  {f.started && f.team_h_score !== null ? (
                    <FixtureItem>
                      <FixtureButton
                        onClick={() => toggle(f.id)}
                        isOpen={isOpen[f.id]}
                      >
                        <Fixture isOpen={isOpen[f.id]} fixture={f} />
                      </FixtureButton>
                      <FixtureStats
                        stats={f.stats}
                        elementsById={elementsById}
                        showElementDialog={showElementDialog}
                        statsByIdentifier={statsByIdentifier}
                        isOpen={isOpen[f.id]}
                      />
                    </FixtureItem>
                  ) : (
                    <FixtureItem>
                      <Fixture fixture={f} />
                    </FixtureItem>
                  )}
                </React.Fragment>
              ))}
            </FixtureList>
          </Box>
        ))}
      </BackgroundColorWrapper>
    </section>
  );
};

export default withTranslation()(Fixtures);
